import { Row } from "@tanstack/react-table";

import { getBadgeVariant } from "../product-detail-page/sub-product-items/table/ItemTable";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import {
  IProductConsumed,
  IProductLogsItem,
} from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";

type UseConsumedTableColumnsProps = {
  getActions: (row: Row<IProductConsumed>) => ActionButtonDataTypes[];
  writePermissions: boolean;
  toggleViewLogSheet: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      itemName: string;
      data: IProductLogsItem[];
    } | null>
  >;
};
export const useConsumedColumns = ({
  getActions,
  writePermissions,
  toggleViewLogSheet,
}: UseConsumedTableColumnsProps): ExtendedColumnDef<IProductConsumed>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (data) => data.name ?? "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.images[0]}
            name={`${row.original.name}`}
            title={`${row.original.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        minWidth: 180,
        showRightBorder: true,
        classNameRow: "max-w-[200px]",
      },
    },
    {
      map: {
        key: "Type",
        valueFrom: "type",
      },
      accessorKey: "Type",
      header: "Type",
      cell: ({ row }) => row.original?.type || "-",
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Model Number",
        dataTransformer: (data): string => {
          return data.modelNumber || "-";
        },
      },
      accessorKey: "Model Number",
      header: "Model Number",
      cell: ({ row }) => row.original.modelNumber || "-",
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Status",
        dataTransformer: (data) => data.status ?? "-",
      },
      accessorKey: "Status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={getBadgeVariant(row.original?.status)}
          className='cursor-pointer'
          onClick={(e) => {
            e.stopPropagation();
            toggleViewLogSheet({
              data: row.original.logs || [],
              isOpen: true,
              itemName: row.original.name,
            });
          }}
        >
          {row.original?.status?.replace(/_/g, " ")}
        </Badge>
      ),
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Site Assigned",
        dataTransformer: (data) => data?.project?.address?.label ?? "-",
      },
      accessorKey: "Site Assigned",
      header: "Site Assigned",
      cell: ({ row }) => row.original?.project?.address?.label || "-",
    },
    {
      id: "actions",
      hideColumn: !writePermissions,
      cell: ({ row }): React.JSX.Element => {
        return (
          <>
            <CustomDropdown data={getActions(row)} />
          </>
        );
      },
      meta: {
        classNameRow: "w-20",
      },
    },
  ];
};
